import { useRef, useEffect } from 'react';
import {
  AdjustmentsHorizontalIcon,
  BookOpenIcon,
  ChevronDownIcon,
  SquaresPlusIcon,
} from '@heroicons/react/16/solid';
import SharedButton from '../../../../../../global/Button/Button';
import { useSearchGridHelp } from '../../../context/SearchGridHelpContext';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { SaveDropdown } from './SaveDropdown';
import { SquaresArrowDownIcon } from './SquaresArrowDownIcon';
import { chemicalRestrictedColumns, defaultModel } from '../../../constants';
import { GridToolbarProps } from '../types';
import { isEventInsideDemoTourTip } from '@/pages/productDemo/productDemoTourHelpers';

export function GridToolbar({
  sourceWeights,
  showSaveDropdown,
  setShowSaveDropdown,
  onSaveComplete,
  onSaveColumnSelection,
  onSaveScore,
  isSaving,
  isAuthenticated,
  isAcademic,
  isTrial,
  view,
  tab,
  isEvidence,
  columnVisibilityModel,
  columnOrder,
  getFilterValueRef,
  searchItems,
  onDownload,
  showEnrichmentTrigger,
  enrichmentSelectionMode,
  onToggleEnrichmentSelection,
  showScoreHistoryAction = false,
  onOpenScoreHistory,
  showControlsButton = false,
  onOpenControls,
}: GridToolbarProps) {
  const help = useSearchGridHelp();
  const saveDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEventInsideDemoTourTip(event)) return;
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target as Node)) {
        setShowSaveDropdown(false);
      }
    };

    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown, setShowSaveDropdown]);

  const getSaveDropdownProps = () => {
    const columnFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];
    const contextParams = ['view', 'tab', 'source', 'idents'];
    const activeFilters = columnFilters.filter(
      (f: any) =>
        !contextParams.includes(f.name) &&
        f.value &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );
    const hasFilters = activeFilters.length > 0;
    const hasSearchItems = searchItems && searchItems.length > 0;

    const baseDefaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictChemical = isAuthenticated && isAcademic;
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);

    const hasCustomVisibility =
      columnVisibilityModel &&
      computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);

    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];

    const hasCustomOrder =
      columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

    const hasCustomColumns = hasCustomVisibility || hasCustomOrder;

    return {
      hasSearchItems,
      hasFilters,
      hasCustomColumns,
    };
  };

  const saveDropdownProps = getSaveDropdownProps();
  const toolbarBtnClass = 'cabtabla-toolbar__btn';
  const downloadLimitLabel = !isAuthenticated ? '10 rows' : isTrial ? '30 rows' : null;
  const canExport = view !== 'ALL';
  const toolsButtonLabel = canExport ? 'Export & tools' : 'Tools';
  const toolsButtonTitle = canExport ? 'Export & tools: enrich, save, download' : 'Tools: enrich, save';
  const toolsButtonAriaLabel = canExport ? 'Export and tools: enrich, save, download' : 'Tools: enrich, save';
  const ToolsIcon = canExport ? SquaresArrowDownIcon : SquaresPlusIcon;

  return (
    <div className="cabtabla-toolbar__actions-inner relative flex flex-nowrap items-center justify-end gap-2">
      {showControlsButton && onOpenControls && (
        <SharedButton
          type="button"
          variant="terciary"
          onClick={onOpenControls}
          aria-label="Open table controls"
          data-testid="grid-controls-sidebar-toggle"
          data-demo-tour="sidebar-toggle"
          size="xs"
          className={toolbarBtnClass}
        >
          <AdjustmentsHorizontalIcon className="w-4 mr-1" />
          Controls
        </SharedButton>
      )}
      <SharedButton
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => help?.openSearchHelp()}
        className={toolbarBtnClass}
        title="Table reference: sources, metrics, and column attributes"
        aria-label="Open table reference"
        data-demo-tour="table-reference"
      >
        <BookOpenIcon className="w-4 mr-1" />
        Reference
      </SharedButton>
      <div className="relative shrink-0" ref={saveDropdownRef}>
        <SharedButton
          variant="secondaryOutline"
          size="xs"
          onClick={() => setShowSaveDropdown(!showSaveDropdown)}
          disabled={(isSaving && !showEnrichmentTrigger) || (!isAuthenticated && !searchItems.length)}
          className={toolbarBtnClass}
          data-demo-tour="actions-button"
          title={toolsButtonTitle}
          aria-label={toolsButtonAriaLabel}
          aria-haspopup="menu"
          aria-expanded={showSaveDropdown}
        >
          <ToolsIcon className="w-5 mr-2" data-testid={canExport ? 'tools-icon-export' : 'tools-icon-only'} />
          {toolsButtonLabel}
          <ChevronDownIcon
            className="w-4 ml-2 transition-transform duration-200"
            style={{ transform: showSaveDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </SharedButton>
        {showSaveDropdown && (
          <SaveDropdown
            {...saveDropdownProps}
            sourceWeights={sourceWeights}
            tab={tab}
            view={view}
            onDownload={ext => {
              onDownload(ext);
              setShowSaveDropdown(false);
            }}
            onSaveComplete={() => {
              onSaveComplete();
              setShowSaveDropdown(false);
            }}
            onSaveColumnSelection={() => {
              onSaveColumnSelection();
              setShowSaveDropdown(false);
            }}
            onSaveScore={() => {
              onSaveScore();
              setShowSaveDropdown(false);
            }}
            showEnrichmentTrigger={showEnrichmentTrigger}
            enrichmentSelectionMode={enrichmentSelectionMode}
            onEnrichmentAction={
              onToggleEnrichmentSelection
                ? () => {
                    onToggleEnrichmentSelection();
                    setShowSaveDropdown(false);
                  }
                : undefined
            }
            showSaveOptions={isAuthenticated && !isAcademic && !isTrial}
            showScoreHistoryAction={showScoreHistoryAction}
            onOpenScoreHistory={() => {
              onOpenScoreHistory?.();
              setShowSaveDropdown(false);
            }}
            downloadLimitLabel={downloadLimitLabel}
          />
        )}
      </div>
    </div>
  );
}
